import { useEffect, useState } from "react";
import { Link } from "wouter";
import { useSeo } from "@/hooks/useSeo";
import FaqSection from "@/components/faq-section";
import { PAGE_FAQS } from "@/data/faqs";
import { useBreadcrumbs } from "@/hooks/useBreadcrumbs";
import { openBookingModal } from "@/components/booking-modal";

export default function ZhivayaMuzyka() {
  const [loaded, setLoaded] = useState(false);

  useSeo({
    title: "Ресторан с живой музыкой в Петербурге — Паруса на крыше",
    description: "Живая музыка в ресторане на крыше Санкт-Петербурга. Джаз, лаунж, кавер-группы — еженедельные выступления с панорамным видом на город. Ул. Марата, 5.",
    keywords: "ресторан с живой музыкой СПб, живая музыка ресторан Петербург, джаз ресторан СПб, живая музыка ужин Петербург, джаз вечер Петербург, ресторан концерт СПб, кавер-группа ресторан Петербург",
    image: "/photos/view_night.jpg",
    canonical: "/zhivaya-muzyka",
  });
  useBreadcrumbs([{ name: "Живая музыка", path: "/zhivaya-muzyka" }]);

  useEffect(() => { setTimeout(() => setLoaded(true), 100); }, []);

  return (
    <div className="w-full bg-background">

      {/* Hero */}
      <section className="relative h-[90vh] w-full flex items-center justify-center overflow-hidden border-b border-primary/20">
        <div className="absolute inset-0 z-0">
          <img
            src="/photos/view_night.webp"
            alt="Живая музыка в ресторане Паруса на крыше — ночной Петербург"
            className="w-full h-full object-cover"
            style={{ filter: "brightness(0.25) saturate(0.7)", transform: "scale(1.05)" }}
          />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(3,13,9,0.99) 0%, rgba(5,20,10,0.65) 50%, rgba(5,20,10,0.3) 100%)" }} />
        </div>
        <div
          className="relative z-10 text-center px-6 max-w-3xl mx-auto"
          style={{ opacity: loaded ? 1 : 0, transform: loaded ? "translateY(0)" : "translateY(30px)", transition: "all 1.2s ease 0.3s" }}
        >
          <div className="inline-block px-6 py-2 border border-primary/30 mb-8">
            <span className="text-[9px] tracking-[0.3em] uppercase text-accent">Живые выступления · Еженедельно</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-serif font-light text-foreground mb-6 leading-[1.1]">
            Живая музыка<br /><span className="text-accent italic">над городом</span>
          </h1>
          <p className="text-white/60 text-lg font-light mb-12 max-w-xl mx-auto leading-relaxed">
            Джаз, лаунж и живые выступления на крыше Петербурга — с панорамным видом на 300° и авторской кухней
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={openBookingModal}
              className="bg-primary/20 border border-primary text-accent px-14 py-5 text-[10px] tracking-[0.35em] uppercase font-medium hover:bg-primary hover:text-background transition-colors"
            >
              Забронировать на вечер с музыкой
            </button>
            <Link
              href="/afisha"
              className="border border-primary/30 text-white/60 px-14 py-5 text-[10px] tracking-[0.35em] uppercase font-medium hover:border-primary/60 hover:text-accent transition-colors"
            >
              Афиша →
            </Link>
          </div>
        </div>
      </section>

      {/* О живой музыке */}
      <section className="py-32 bg-card border-b border-primary/10">
        <div className="container mx-auto px-6 md:px-14 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div>
              <div className="text-[9px] tracking-[0.4em] text-primary/50 uppercase mb-5">Музыка и атмосфера</div>
              <h2 className="text-4xl md:text-5xl font-serif font-light text-foreground mb-8 leading-tight">
                Музыка, которая<br /><span className="text-accent italic">меняет вечер</span>
              </h2>
              <p className="text-muted-foreground font-light leading-relaxed mb-6">
                Живая музыка в «Парусах на крыше» — не фоновый шум. Это выступления профессиональных музыкантов, которые звучат именно так, как должна звучать музыка за хорошим ужином: достаточно громко, чтобы создавать атмосферу, и достаточно тихо, чтобы можно было разговаривать.
              </p>
              <p className="text-muted-foreground font-light leading-relaxed mb-10">
                Джазовые квартеты, сольные пианисты, акустические трио, кавер-группы — репертуар меняется каждую неделю. Расписание публикуется в разделе «Афиша» заранее, чтобы вы могли выбрать именно тот вечер, который подходит вам по настроению.
              </p>
              <Link
                href="/afisha"
                className="inline-block border border-primary/40 text-accent px-10 py-4 text-[10px] tracking-[0.3em] uppercase hover:border-primary transition-colors"
              >
                Смотреть афишу →
              </Link>
            </div>
            <div>
              <img
                src="/photos/view_sunset.webp"
                alt="Вечер с живой музыкой в ресторане Паруса на крыше"
                className="w-full aspect-video object-cover border border-primary/10"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Форматы */}
      <section className="py-32 bg-background border-b border-primary/10">
        <div className="container mx-auto px-6 md:px-14 max-w-6xl">
          <div className="mb-16">
            <div className="text-[9px] tracking-[0.4em] text-primary/50 uppercase mb-4">Форматы выступлений</div>
            <h2 className="text-4xl md:text-5xl font-serif font-light text-foreground">Музыка на любой вкус</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: "♩",
                title: "Джазовый квартет",
                desc: "Классический джаз — контрабас, ударные, фортепиано, вокал. Стандарты Синатры, Эллы Фицджеральд и авторские аранжировки. Идеально для романтического ужина.",
              },
              {
                icon: "♩",
                title: "Акустическое трио",
                desc: "Камерный формат: гитара, виолончель или скрипка, вокал. Лёгкая, ненавязчивая музыка, которая создаёт атмосферу, не мешая разговору.",
              },
              {
                icon: "♩",
                title: "Лаунж и боса-нова",
                desc: "Бразильская лёгкость боса-новы и расслабленный лаунж — идеальный саундтрек для летнего вечера на открытой террасе.",
              },
              {
                icon: "♩",
                title: "Соло фортепиано",
                desc: "Живое фортепиано создаёт особую атмосферу утончённости. Классика, джаз, современные интерпретации — в зависимости от исполнителя.",
              },
              {
                icon: "♩",
                title: "Кавер-программа",
                desc: "Популярные хиты в живом исполнении — от мировых классиков до современных хитов. Энергичный формат для праздников и корпоративов.",
              },
              {
                icon: "♩",
                title: "Тематические вечера",
                desc: "Джазовые пятницы, латиноамериканские субботы, романтические ужины с квартетом — специальные программы по особым датам.",
              },
            ].map((item) => (
              <div key={item.title} className="group p-6 border border-primary/15 hover:border-primary/40 transition-colors">
                <div className="text-primary/40 mb-4 text-2xl font-serif">{item.icon}</div>
                <h3 className="font-['Cormorant_SC'] text-sm tracking-[0.15em] text-accent mb-3">{item.title}</h3>
                <p className="text-xs text-muted-foreground font-light leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Расписание */}
      <section className="py-28 bg-card border-b border-primary/10">
        <div className="container mx-auto px-6 md:px-14 max-w-5xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <div className="text-[9px] tracking-[0.4em] text-primary/50 uppercase mb-5">Когда приходить</div>
              <h2 className="text-3xl md:text-4xl font-serif font-light text-foreground mb-8 leading-tight">
                Живая музыка<br /><span className="text-accent italic">по вечерам</span>
              </h2>
              <p className="text-muted-foreground font-light leading-relaxed mb-8">
                Живые выступления в «Парусах» проходят еженедельно по пятницам и субботам — это основные вечера. В остальные дни — по специальному расписанию, которое публикуется в афише.
              </p>
              <div className="space-y-4 mb-8">
                {[
                  { day: "Пятница", time: "19:00–22:00", note: "Джаз или акустика, атмосферный старт выходных" },
                  { day: "Суббота", time: "19:00–22:00", note: "Кавер-программы, лаунж, тематические вечера" },
                  { day: "Спец. события", time: "По расписанию", note: "14 февраля, 8 Марта, Новый год, день города" },
                ].map((item) => (
                  <div key={item.day} className="flex items-start gap-5 py-4 border-b border-primary/10 last:border-0">
                    <div className="w-28 shrink-0">
                      <div className="text-accent text-sm font-light">{item.day}</div>
                      <div className="text-primary/50 text-xs">{item.time}</div>
                    </div>
                    <div className="text-muted-foreground text-xs font-light leading-relaxed">{item.note}</div>
                  </div>
                ))}
              </div>
              <p className="text-muted-foreground/60 text-xs font-light">
                Актуальное расписание — в разделе «Афиша». Обновляется еженедельно.
              </p>
            </div>
            <div className="space-y-6">
              <div className="p-8 border border-primary/15 bg-card/50">
                <div className="text-[9px] tracking-[0.4em] text-primary/50 uppercase mb-4">Совет</div>
                <p className="text-muted-foreground font-light leading-relaxed text-sm">
                  Для вечера с музыкой бронируйте столик минимум за 2–3 дня. В пятницу и субботу вечерние места у окна разбирают быстро — особенно летом в сезон белых ночей.
                </p>
              </div>
              <div className="p-8 border border-primary/15 bg-card/50">
                <div className="text-[9px] tracking-[0.4em] text-primary/50 uppercase mb-4">Особые поводы</div>
                <p className="text-muted-foreground font-light leading-relaxed text-sm mb-4">
                  Хотите живую музыку специально для вашего праздника? Для банкетов и частных мероприятий мы можем организовать музыкальную программу под ключ.
                </p>
                <Link
                  href="/banket"
                  className="text-accent text-xs font-light tracking-wider hover:underline"
                >
                  Подробнее о банкетах →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Почему вживую */}
      <section className="py-28 bg-background border-b border-primary/10">
        <div className="container mx-auto px-6 md:px-14 max-w-5xl text-center">
          <div className="text-[9px] tracking-[0.4em] text-primary/50 uppercase mb-5">Живая музыка vs плейлист</div>
          <h2 className="text-3xl md:text-4xl font-serif font-light text-foreground mb-8">Почему вживую — совсем другое</h2>
          <p className="text-muted-foreground font-light leading-relaxed max-w-2xl mx-auto mb-12">
            Запись и живое исполнение — принципиально разный опыт. Живые музыканты реагируют на зал: они чувствуют настроение и меняют темп, динамику, репертуар. Именно это создаёт то самое ощущение «вечер удался», которое невозможно воспроизвести плейлистом.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
            {[
              { title: "Уникальность", body: "Каждое выступление неповторимо — никакой записи не создать то же настроение, что рождается в зале между музыкантами и гостями" },
              { title: "Эмоция", body: "Живое звучание воздействует физически — вибрации, динамика, дыхание музыкантов. Это чувствуется телом, а не только ушами" },
              { title: "Атмосфера", body: "Присутствие музыкантов в зале трансформирует пространство — обычный ресторан превращается в место, куда хочется вернуться" },
            ].map((item) => (
              <div key={item.title} className="p-6 border border-primary/15">
                <h3 className="font-['Cormorant_SC'] text-sm tracking-[0.15em] text-accent mb-3">{item.title}</h3>
                <p className="text-xs text-muted-foreground font-light leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FaqSection items={PAGE_FAQS["/zhivaya-muzyka"]} title="Вопросы о живой музыке" />

      {/* CTA */}
      <section className="py-28 bg-background">
        <div className="container mx-auto px-6 text-center max-w-2xl">
          <h2 className="text-4xl font-serif font-light text-foreground mb-6">Забронируйте вечер с музыкой</h2>
          <p className="text-muted-foreground font-light mb-10 leading-relaxed">
            Проверьте афишу, выберите вечер с выступлением и забронируйте столик. Или позвоните — уточним расписание и подберём лучшее место.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={openBookingModal}
              className="bg-primary border border-primary text-background px-12 py-4 text-[10px] tracking-[0.3em] uppercase font-medium hover:bg-primary/80 transition-colors"
            >
              Забронировать столик
            </button>
            <Link
              href="/afisha"
              className="border border-primary/40 text-accent px-12 py-4 text-[10px] tracking-[0.3em] uppercase font-medium hover:border-primary transition-colors"
            >
              Смотреть афишу
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
